import { Directive, ElementRef, HostListener, OnDestroy, inject, input, output } from '@angular/core';

export interface HoverCardPosition {
  x: number;
  y: number;
}

export type HoverCardPlacement = 'above' | 'below' | 'center';

@Directive({
  selector: '[appHoverCard]',
  standalone: true,
})
export class HoverCardDirective implements OnDestroy {
  private elementRef = inject(ElementRef);

  readonly hoverDelay = input<number>(300); // Delay before showing in milliseconds
  readonly cardWidth = input<number>(600); // Default card width
  readonly cardHeight = input<number>(200); // Estimated card height
  readonly placement = input<HoverCardPlacement>('below'); // Where to position the card
  readonly screenPadding = input<number>(8); // Padding from screen edges

  readonly hoverShow = output<HoverCardPosition>();
  readonly hoverHide = output<void>();

  private timeout: ReturnType<typeof setTimeout> | null = null;

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent): void {
    // Clear any existing timeout
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    const position = this.calculatePosition(event);

    // Show with delay
    this.timeout = setTimeout(() => {
      this.hoverShow.emit(position);
    }, this.hoverDelay());
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    // Clear timeout if hovering stopped before delay
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    this.hoverHide.emit();
  }

  private calculatePosition(event: MouseEvent): HoverCardPosition {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Adjust card width based on viewport
    const actualCardWidth = Math.min(this.cardWidth(), viewportWidth * 0.95);

    let xPosition: number;
    let yPosition: number;

    switch (this.placement()) {
      case 'above':
        // Position above the element
        xPosition = rect.left + rect.width / 2 - actualCardWidth / 2;
        yPosition = rect.top - this.cardHeight() - this.screenPadding();
        break;

      case 'below':
        // Position below the element
        xPosition = rect.left + rect.width / 2 - actualCardWidth / 2;
        yPosition = rect.bottom + this.screenPadding();
        break;

      case 'center':
      default:
        // Center on viewport
        xPosition = (viewportWidth - actualCardWidth) / 2;
        yPosition = (viewportHeight - this.cardHeight()) / 2;
        break;
    }

    // Adjust if card would go off the left edge
    if (xPosition < this.screenPadding()) {
      xPosition = this.screenPadding();
    }

    // Adjust if card would go off the right edge
    if (xPosition + actualCardWidth > viewportWidth - this.screenPadding()) {
      xPosition = viewportWidth - actualCardWidth - this.screenPadding();
    }

    // Adjust if card would go off the top edge
    if (yPosition < this.screenPadding()) {
      yPosition = this.screenPadding();
    }

    // Adjust if card would go off the bottom edge
    if (yPosition + this.cardHeight() > viewportHeight - this.screenPadding()) {
      yPosition = viewportHeight - this.cardHeight() - this.screenPadding();
    }

    return { x: xPosition, y: yPosition };
  }

  ngOnDestroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }
}
